import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertCircle, RotateCcw, Smartphone, AlertTriangle, UserCheck } from "lucide-react";
import { api, formatApiError, API, authQuery } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CompassLogo } from "../components/CompassLogo";

/** Build an authenticated URL for a member-photo storage path so that the
 * `<img>` element triggers a request the backend can serve via /api/photo. */
function photoSrc(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${API}/photo?path=${encodeURIComponent(path)}${authQuery().replace("?", "&")}`;
}

/**
 * NFC check-in landing page.
 *
 * Cross-lodge aware: a passport from another lodge in the same Square & Compass
 * platform can be tapped here. We display home-lodge info and either check the
 * visitor in or — for non-Active/Honorary statuses — show a big red ALARM with
 * the home lodge's reason and the date the status was changed.
 */
export default function CheckInPage() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const locale =t("checkIn.en_gb");
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", payload: null, error: null });

  useEffect(() => {
    if (loading || user === null) return;
    if (user === false || user?.role === "superadmin") return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/attendance/check-in", { token });
        if (cancelled) return;
        if (data.alarm) {
          setState({ status: "alarm", payload: data, error: null });
        } else if (data.view_only) {
          setState({ status: "view_only", payload: data, error: null });
        } else if (data.already) {
          setState({ status: "already", payload: data, error: null });
        } else {
          setState({ status: "ok", payload: data, error: null });
        }
      } catch (err) {
        if (cancelled) return;
        setState({ status: "error", payload: null, error: formatApiError(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, user, loading]);

  if (!loading && user === false) {
    return <Navigate to={`/login?next=${encodeURIComponent(`/check-in/${token}`)}`} replace />;
  }
  if (user?.role === "superadmin") {
    return (
      <div className="auth-bg flex items-center justify-center" style={{ minHeight: "100vh" }}>
        <div className="gold-card" style={{ maxWidth: 520, padding: "2rem", textAlign: "center" }}>
          <h2 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.16em", color: "#0d1b2a" }}>{t("attendance.checkInPage")}</h2>
          <p style={{ marginTop: "1rem", color: "#34322d" }}>
            {t("checkIn.sign_in_with_a_lodge")}
          </p>
        </div>
      </div>
    );
  }

  // ALARM screen — full-bleed red, dominant
  if (state.status === "alarm") {
    return <AlarmScreen payload={state.payload} t={t} i18n={i18n} locale={locale} />;
  }

  // View-only profile (no active event)
  if (state.status === "view_only") {
    return <ViewOnlyProfile payload={state.payload} t={t} i18n={i18n} locale={locale} navigate={navigate} />;
  }

  const member = state.payload?.member;
  const ev = state.payload?.event;
  const isVisitor = state.payload?.is_visitor;
  const homeLodge = state.payload?.home_lodge;

  return (
    <div className="auth-bg" style={{ minHeight: "100vh", padding: "1.5rem", display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="check-in-page">
      <div className="gold-card" style={{ maxWidth: 620, width: "100%", padding: "2.5rem 2rem", textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "1rem" }}>
          <CompassLogo size={64} />
        </div>
        <div className="kv-label" style={{ fontSize: "0.7rem", letterSpacing: "0.22em", color: "#9a7530", marginBottom: "1.4rem" }}>
          {t("attendance.checkInPage")} · NFC
        </div>

        {state.status === "loading" && (
          <>
            <div className="spinner" style={{ margin: "0 auto 1.2rem" }} />
            <p style={{ color: "#34322d" }}>
              <Smartphone size={14} style={{ display: "inline", marginRight: 6, verticalAlign: "-2px" }} />
              {t("attendance.checkInWaiting")}
            </p>
          </>
        )}

        {(state.status === "ok" || state.status === "already") && member && ev && (
          <CheckInResult
            iconColor={state.status === "already" ? "#9a7530" : "#3e6a3c"}
            Icon={state.status === "already" ? CheckCircle2 : (isVisitor ? UserCheck : CheckCircle2)}
            title={
              state.status === "already"
                ? t("attendance.checkInAlready", { name: `${member.first_name} ${member.last_name}` })
                : t("attendance.checkInSuccess", { name: `${member.first_name} ${member.last_name}` })
            }
            visitor={isVisitor ? homeLodge : null}
            member={member}
            ev={ev}
            recorded={state.payload?.attendance?.recorded_at}
            locale={locale}
            t={t}
            i18n={i18n}
            navigate={navigate}
          />
        )}

        {state.status === "error" && (
          <>
            <AlertCircle size={56} color="#7c2a2a" style={{ display: "block", margin: "0 auto 1rem" }} />
            <h2 style={{ fontFamily: "Cinzel, serif", color: "#7c2a2a", fontSize: "1.2rem", letterSpacing: "0.1em" }}>
              {t("attendance.checkInError")}
            </h2>
            <p style={{ color: "#34322d", marginTop: "0.6rem", fontSize: "0.95rem" }}>{state.error}</p>
            <div style={{ display: "flex", gap: "0.6rem", justifyContent: "center", marginTop: "1.4rem", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn-outline-gold"
                onClick={() => navigate("/scan")}
                data-testid="check-in-retry"
              >
                <RotateCcw size={12} style={{ display: "inline", marginRight: 4 }} /> {t("common.continue", "Următorul")}
              </button>
              <button
                type="button"
                className="btn-outline-gold"
                onClick={() => navigate("/attendance")}
                data-testid="check-in-back-app"
              >
                {t("checkIn.back_to_app")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CheckInResult({ iconColor, Icon, title, ev, recorded, locale, t, i18n, navigate, visitor, member }) {
  const grade = member?.grade;
  const photoUrl = photoSrc(member?.photo_url);
  const lodgeName = visitor?.name;
  return (
    <>
      <Icon size={56} color={iconColor} style={{ display: "block", margin: "0 auto 0.8rem" }} />
      {photoUrl && (
        <img
          src={photoUrl}
          alt={`${member.first_name} ${member.last_name}`}
          data-testid="checkin-member-photo"
          style={{
            width: 120,
            height: 120,
            borderRadius: "50%",
            objectFit: "cover",
            border: "3px solid #c9a84c",
            margin: "0 auto 0.9rem",
            display: "block",
            boxShadow: "0 4px 18px -4px rgba(0,0,0,0.25)",
          }}
        />
      )}
      <h2
        style={{
          fontFamily: "Cinzel, serif",
          fontSize: "1.4rem",
          letterSpacing: "0.06em",
          color: "#0d1b2a",
          marginBottom: "0.4rem",
        }}
      >
        {title}
      </h2>
      {grade && (
        <div
          className="kv-label"
          style={{ fontSize: "0.78rem", letterSpacing: "0.16em", color: "#9a7530" }}
          data-testid="checkin-member-grade"
        >
          {grade}
        </div>
      )}
      {visitor && (
        <div
          data-testid="visitor-badge"
          style={{
            display: "inline-block",
            marginTop: "0.7rem",
            padding: "0.35rem 0.9rem",
            background: "#0d1b2a",
            color: "#e8c97a",
            border: "1px solid #c9a84c",
            fontFamily: "Cinzel, serif",
            fontSize: "0.7rem",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          {t("attendance.visitorFrom", "Vizitator de la")} · {lodgeName || "—"}
        </div>
      )}
      {!visitor && member?.lodge_name && (
        <div
          className="kv-label"
          data-testid="checkin-home-lodge"
          style={{ marginTop: "0.5rem", fontSize: "0.7rem", letterSpacing: "0.14em", color: "#0d1b2a" }}
        >
          {member.lodge_name}
        </div>
      )}
      {(member?.status || member?.date_initiated) && (
        <div
          style={{
            marginTop: "1rem",
            padding: "0.6rem 0.9rem",
            background: "#fdf4e0",
            border: "1px solid #c9a84c",
            color: "#7a4f1c",
            fontSize: "0.78rem",
            display: "inline-block",
          }}
          data-testid="checkin-member-meta"
        >
          {member?.status && (
            <div>
              <strong>{t("members.status")}:</strong> {member.status}
            </div>
          )}
          {member?.date_initiated && (
            <div>
              <strong>{t("members.dateInitiated")}:</strong>{" "}
              {new Date(member.date_initiated).toLocaleDateString(locale)}
            </div>
          )}
          {member?.date_promoted && (
            <div>
              <strong>{t("members.datePromoted")}:</strong>{" "}
              {new Date(member.date_promoted).toLocaleDateString(locale)}
            </div>
          )}
        </div>
      )}
      <div style={{ marginTop: "1rem", color: "#34322d" }}>
        <div className="kv-label" style={{ marginTop: "0.3rem" }}>{t("attendance.atEvent")}</div>
        <div style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", fontSize: "1.05rem", marginTop: "0.2rem" }}>
          {ev.title}
        </div>
        <div className="kv-label" style={{ marginTop: "0.4rem" }}>
          {new Date(ev.date).toLocaleString(locale, { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
        </div>
        {recorded && (
          <div className="kv-label" style={{ marginTop: "0.6rem", fontSize: "0.7rem" }}>
            ⏱ {new Date(recorded).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: "0.6rem", justifyContent: "center", marginTop: "1.6rem", flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn-primary-gold"
          onClick={() => navigate("/scan")}
          data-testid="check-in-next"
        >
          {t("common.continue", "Următorul")}
        </button>
        <button
          type="button"
          className="btn-outline-gold"
          onClick={() => navigate(`/attendance/${ev.id}`)}
          data-testid="check-in-view"
        >
          {t("attendance.viewDetails")}
        </button>
        <button
          type="button"
          className="btn-outline-gold"
          onClick={() => navigate("/attendance")}
          data-testid="check-in-back-app"
        >
          {t("checkIn.back_to_app")}
        </button>
      </div>
    </>
  );
}

function AlarmScreen({ payload, t, i18n, locale }) {
  const navigateRef = useNavigate();
  const m = payload.member;
  const home = payload.home_lodge;
  const isVisitor = payload.is_visitor;
  const statusKey = (m.status || "").toLowerCase();
  return (
    <div
      data-testid="alarm-screen"
      style={{
        minHeight: "100vh",
        background: "#7c2a2a",
        backgroundImage: "linear-gradient(180deg, #8a2929 0%, #5a1212 100%)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
      }}
    >
      <div
        style={{
          maxWidth: 720,
          width: "100%",
          background: "#fff",
          color: "#0d1b2a",
          border: "6px solid #7c2a2a",
          padding: "2.5rem 2rem",
          textAlign: "center",
          boxShadow: "0 12px 60px -10px rgba(0,0,0,0.6)",
        }}
      >
        <AlertTriangle
          size={88}
          color="#7c2a2a"
          style={{ display: "block", margin: "0 auto 1.2rem", animation: "pulse-alarm 1.6s ease-in-out infinite" }}
        />
        {m.photo_url && (
          <img
            src={photoSrc(m.photo_url)}
            alt={`${m.first_name} ${m.last_name}`}
            style={{
              width: 110,
              height: 110,
              borderRadius: "50%",
              objectFit: "cover",
              border: "3px solid #7c2a2a",
              margin: "0 auto 1rem",
              display: "block",
            }}
          />
        )}
        <div
          style={{
            fontFamily: "Cinzel, serif",
            fontSize: "1.05rem",
            letterSpacing: "0.4em",
            textTransform: "uppercase",
            color: "#7c2a2a",
            marginBottom: "0.8rem",
          }}
        >
          {t(`attendance.alarmTitle.${statusKey}`, { defaultValue: t("attendance.alarmTitleGeneric", "Atenție") })}
        </div>
        <h1
          style={{
            fontFamily: "Cinzel, serif",
            fontSize: "2.2rem",
            color: "#0d1b2a",
            marginBottom: "0.4rem",
            letterSpacing: "0.04em",
          }}
        >
          {m.first_name} {m.last_name}
        </h1>
        <div className="kv-label" style={{ fontSize: "0.78rem" }}>
          {m.grade}
          {isVisitor && home && (
            <>
              {" · "}
              <span style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.14em", color: "#7c2a2a" }}>
                {(home.name || "").toUpperCase()}
              </span>
            </>
          )}
        </div>
        <div
          style={{
            marginTop: "1.6rem",
            padding: "1rem 1.2rem",
            background: "#fdecec",
            border: "1px solid #d9b9b9",
            color: "#7c2a2a",
          }}
        >
          <div className="kv-label" style={{ color: "#7c2a2a" }}>{t("members.status")}</div>
          <div style={{ fontFamily: "Cinzel, serif", fontSize: "1.4rem", fontWeight: 600, marginTop: "0.2rem", textTransform: "uppercase" }}>
            {m.status}
          </div>
          {m.status_reason && (
            <p style={{ marginTop: "0.6rem", fontStyle: "italic", color: "#5a1212" }}>{m.status_reason}</p>
          )}
          {m.status_changed_at && (
            <div className="kv-label" style={{ color: "#7c2a2a", marginTop: "0.4rem", fontSize: "0.66rem" }}>
              {t("attendance.statusChangedOn", "Schimbat")}:{" "}
              {new Date(m.status_changed_at).toLocaleString(locale, {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
              {m.status_changed_by ? ` · ${m.status_changed_by}` : ""}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: "0.6rem", justifyContent: "center", marginTop: "1.6rem", flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn-primary-gold"
            onClick={() => navigateRef("/scan")}
            style={{ minWidth: 200 }}
            data-testid="alarm-dismiss"
          >
            {t("frozen.ok")}
          </button>
          <button
            type="button"
            className="btn-outline-gold"
            onClick={() => navigateRef("/attendance")}
            data-testid="alarm-back-app"
          >
            {t("checkIn.back_to_app")}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes pulse-alarm {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.08); opacity: 0.85; }
        }
      `}</style>
    </div>
  );
}

function ViewOnlyProfile({ payload, t, i18n, locale, navigate }) {
  const m = payload.member;
  const home = payload.home_lodge;
  const isVisitor = payload.is_visitor;
  const [requesting, setRequesting] = React.useState(false);
  const [requested, setRequested] = React.useState(false);

  const requestTransfer = async () => {
    setRequesting(true);
    try {
      await api.post("/transfers/request", { member_id: m.id });
      setRequested(true);
    } catch (err) {
      alert(formatApiError(err));
    } finally {
      setRequesting(false);
    }
  };

  const fields = [
    { label: t("members.dateOfBirth", "Data nașterii"), value: m.date_of_birth },
    { label: t("members.dateInitiated", "Data inițierii"), value: m.date_initiated },
    { label: t("members.datePromoted", "Data promovării"), value: m.date_promoted },
    { label: t("members.profession", "Profesie"), value: m.profession },
    { label: t("common.phone", "Telefon"), value: m.phone },
    { label: t("common.email", "Email"), value: m.email },
    { label: t("members.address", "Adresă"), value: m.address },
  ];

  return (
    <div className="auth-bg" style={{ minHeight: "100vh", padding: "1.5rem", display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="view-only-profile">
      <div className="gold-card" style={{ maxWidth: 620, width: "100%", padding: "2rem 1.6rem" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.8rem" }}>
          <CompassLogo size={48} />
        </div>
        <div className="kv-label" style={{ fontSize: "0.66rem", letterSpacing: "0.22em", color: "#9a7530", textAlign: "center", marginBottom: "0.4rem" }}>
          {t("checkIn.view_only_no_active_event")}
        </div>
        {m.photo_url && (
          <img
            src={photoSrc(m.photo_url)}
            alt={`${m.first_name} ${m.last_name}`}
            data-testid="viewonly-photo"
            style={{
              width: 110,
              height: 110,
              borderRadius: "50%",
              objectFit: "cover",
              border: "3px solid #c9a84c",
              margin: "0.4rem auto 0.6rem",
              display: "block",
            }}
          />
        )}
        <h2 style={{ fontFamily: "Cinzel, serif", fontSize: "1.5rem", color: "#0d1b2a", textAlign: "center", letterSpacing: "0.04em", marginBottom: "0.2rem" }}>
          {m.first_name} {m.last_name}
        </h2>
        <div className="kv-label" style={{ textAlign: "center" }}>
          {m.grade}
          {m.status && m.status !== "Active" && <span> · {m.status}</span>}
        </div>
        {isVisitor && home && (
          <div style={{ textAlign: "center", marginTop: "0.6rem" }}>
            <span
              data-testid="visitor-badge"
              style={{
                display: "inline-block",
                padding: "0.25rem 0.8rem",
                background: "#0d1b2a",
                color: "#e8c97a",
                border: "1px solid #c9a84c",
                fontFamily: "Cinzel, serif",
                fontSize: "0.65rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              {t("attendance.visitorFrom")} · {home.name || "—"}
            </span>
          </div>
        )}
        <div className="gold-divider my-4" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "0.4rem" }}>
          {fields.filter((f) => f.value).map((f) => (
            <div key={f.label} className="kv-row">
              <span className="kv-label">{f.label}</span>
              <span className="kv-value">{f.value}</span>
            </div>
          ))}
          {fields.filter((f) => f.value).length === 0 && (
            <div style={{ color: "#858481", fontStyle: "italic", fontSize: "0.85rem", textAlign: "center" }}>
              {t("checkIn.no_additional_public_info")}
            </div>
          )}
        </div>
        {m.notes && (
          <>
            <div className="gold-divider my-4" />
            <div className="kv-label">{t("common.notes", "Notițe")}</div>
            <p style={{ marginTop: "0.4rem", whiteSpace: "pre-wrap", fontSize: "0.9rem", color: "#34322d" }}>{m.notes}</p>
          </>
        )}

        <div style={{ display: "flex", gap: "0.6rem", justifyContent: "center", marginTop: "1.6rem", flexWrap: "wrap" }}>
          {isVisitor && !requested && (
            <button
              type="button"
              className="btn-primary-gold"
              onClick={requestTransfer}
              disabled={requesting}
              data-testid="request-transfer-btn"
            >
              {requesting
                ? "..."
                : t("checkIn.request_transfer_to_my_lodge")}
            </button>
          )}
          {requested && (
            <div
              style={{
                background: "#e8f0e5",
                border: "1px solid #b9cfb4",
                color: "#3e6a3c",
                padding: "0.6rem 1rem",
                fontFamily: "Cinzel, serif",
                fontSize: "0.78rem",
                letterSpacing: "0.12em",
              }}
              data-testid="transfer-requested-banner"
            >
              {t("checkIn.transfer_requested")}
            </div>
          )}
          <button
            type="button"
            className="btn-outline-gold"
            onClick={() => navigate("/scan")}
            data-testid="check-in-back"
          >
            {t("common.continue", "Următorul")}
          </button>
          <button
            type="button"
            className="btn-outline-gold"
            onClick={() => navigate("/attendance")}
            data-testid="viewonly-back-app"
          >
            {t("checkIn.back_to_app")}
          </button>
        </div>
      </div>
    </div>
  );
}
